export type WeatherFeatureServerLayer = {
  id: string;
  name: string;
  /** Grouping shown in the picker, e.g. "Watches and warnings". */
  category: string;
  description: string;
  serviceUrl: string;
};

/**
 * Public NOAA/NWS FeatureServer layers offered when a rule points at a custom
 * ArcGIS feed, so an author does not have to paste a URL by hand.
 *
 * Every entry is a layer URL rather than a service root: the field picker reads
 * its schema straight from this URL and cannot resolve a root on its own.
 */
export const WEATHER_FEATURESERVER_CATALOG: WeatherFeatureServerLayer[] = [
  {
    id: 'nws-events-by-severity',
    name: 'NWS events by size and severity',
    category: 'Watches and warnings',
    description: 'All active NWS watches, warnings, and advisories.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/6',
  },
  {
    id: 'nws-extreme-events',
    name: 'NWS extreme events',
    category: 'Watches and warnings',
    description: 'Active NWS events classified as extreme severity.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/8',
  },
  {
    id: 'nws-severe-events',
    name: 'NWS severe events',
    category: 'Watches and warnings',
    description: 'Active NWS events classified as severe severity.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NWS_Watches_Warnings_v1/FeatureServer/9',
  },
  {
    id: 'hurricanes-forecast-cone',
    name: 'Hurricane forecast cone',
    category: 'Tropical',
    description: 'NHC forecast error cone and danger area polygons.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/4',
  },
  {
    id: 'hurricanes-forecast-position',
    name: 'Hurricane forecast position',
    category: 'Tropical',
    description: 'NHC forecast track points with wind speed and category.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/0',
  },
  {
    id: 'hurricanes-observed-position',
    name: 'Hurricane observed position',
    category: 'Tropical',
    description: 'NHC observed storm positions to date.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/1',
  },
  {
    id: 'ndfd-precipitation-total',
    name: 'Forecast precipitation total',
    category: 'Precipitation and water',
    description: 'NDFD cumulative forecast precipitation amounts.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/NDFD_Precipitation_v1/FeatureServer/2',
  },
  {
    id: 'live-stream-gauges',
    name: 'Live stream gauges',
    category: 'Precipitation and water',
    description: 'USGS/NWS river gauges with live stage and flow readings.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Live_Stream_Gauges_v1/FeatureServer/0',
  },
  {
    id: 'wildfire-perimeters',
    name: 'Current wildfire perimeters',
    category: 'Fire',
    description: 'Active US wildfire perimeters with acreage attributes.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/1',
  },
  {
    id: 'wildfire-incidents',
    name: 'Current wildfire incidents',
    category: 'Fire',
    description: 'Active US wildfire incident points with containment detail.',
    serviceUrl:
      'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0',
  },
];

export function normalizeWeatherServiceUrl(serviceUrl: string): string {
  return serviceUrl.trim().replace(/\/+$/, '');
}

export function findWeatherLayerByUrl(
  serviceUrl: string | null | undefined
): WeatherFeatureServerLayer | undefined {
  if (!serviceUrl) return undefined;
  const normalized = normalizeWeatherServiceUrl(serviceUrl);
  return WEATHER_FEATURESERVER_CATALOG.find(
    (layer) => normalizeWeatherServiceUrl(layer.serviceUrl) === normalized
  );
}

/** Catalog entries grouped by category, in first-seen order. */
export function weatherLayersByCategory(): {
  label: string;
  items: string[];
}[] {
  const groups: { label: string; items: string[] }[] = [];
  for (const layer of WEATHER_FEATURESERVER_CATALOG) {
    const existing = groups.find((group) => group.label === layer.category);
    if (existing) {
      existing.items.push(layer.serviceUrl);
    } else {
      groups.push({ label: layer.category, items: [layer.serviceUrl] });
    }
  }
  return groups;
}
